import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Alert, Chip,
  Stepper, Step, StepLabel, StepContent, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  PersonAdd as PersonAddIcon,
  Celebration as CelebrationIcon,
  SkipNext as SkipIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { preferencesApi, calendarConnectApi, authApi } from '../api/services';

// Microsoft icon SVG inline
function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

// Google icon SVG inline
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Restore wizard state after OAuth redirect (user navigated away and came back)
  const savedStep = sessionStorage.getItem('onboarding-step');
  const urlParams = new URLSearchParams(window.location.search);
  const cameBackFromOAuth = urlParams.get('connected') === 'microsoft' || urlParams.get('connected') === 'google';

  const [activeStep, setActiveStep] = useState(cameBackFromOAuth ? 1 : (savedStep ? parseInt(savedStep) : 0));

  // Calendar step state
  const [calendarConnected, setCalendarConnected] = useState(cameBackFromOAuth);
  const [calendarSkipped, setCalendarSkipped] = useState(false);
  const [isPersonal, setIsPersonal] = useState(false);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [calendarError, setCalendarError] = useState<string | null>(null);

  // Working hours step state
  const [workDayStart, setWorkDayStart] = useState('08:00');
  const [workDayEnd, setWorkDayEnd] = useState('18:00');
  const [lunchStartMinute, setLunchStartMinute] = useState(720);
  const [lunchDurationMinutes, setLunchDurationMinutes] = useState(60);
  const [hoursSaved, setHoursSaved] = useState(false);
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursError, setHoursError] = useState<string | null>(null);

  // EA invite step state
  const [eaName, setEaName] = useState('');
  const [eaEmail, setEaEmail] = useState('');
  const [eaInvited, setEaInvited] = useState(false);
  const [eaSkipped, setEaSkipped] = useState(false);
  const [eaInviting, setEaInviting] = useState(false);
  const [eaError, setEaError] = useState<string | null>(null);

  const displayName = user?.displayName?.split(' ')[0] || 'there';

  // Auto-advance to working hours after a successful OAuth return.
  // Strip the ?connected= param so a refresh doesn't re-trigger this.
  useEffect(() => {
    if (cameBackFromOAuth) {
      window.history.replaceState({}, '', window.location.pathname);
      const timer = setTimeout(() => setActiveStep(2), 500);
      return () => clearTimeout(timer);
    }
  }, [cameBackFromOAuth]);

  const handleConnectCalendar = async (provider: 'microsoft' | 'google') => {
    setConnecting(provider);
    setCalendarError(null);
    try {
      const name = provider === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      const data = provider === 'microsoft'
        ? await calendarConnectApi.connectMicrosoft({
            name,
            isPersonal,
            eaVisibility: isPersonal ? 'TimeOnly' : undefined,
          })
        : await calendarConnectApi.connectGoogle({
            name,
            isPersonal,
            eaVisibility: isPersonal ? 'TimeOnly' : undefined,
          });
      if (data.authUrl) {
        // Save wizard state before navigating away to OAuth
        sessionStorage.setItem('onboarding-step', '1');
        // Navigate in the same window — user comes back to /getting-started?connected=...
        window.location.href = data.authUrl;
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to start connection';
      setCalendarError(typeof msg === 'string' ? msg : 'Failed to start connection');
      setConnecting(null);
    }
  };

  const handleSaveHours = async () => {
    setHoursSaving(true);
    setHoursError(null);
    try {
      const toTimeOnly = (v: string) => v.length === 5 ? `${v}:00` : v;
      await preferencesApi.update({
        workDayStart: toTimeOnly(workDayStart),
        workDayEnd: toTimeOnly(workDayEnd),
        lunchStartMinute,
        lunchDurationMinutes,
      });
      setHoursSaved(true);
      setTimeout(() => setActiveStep(3), 500);
    } catch (err: any) {
      const data = err.response?.data;
      let msg = 'Failed to save';
      if (typeof data === 'string') msg = data;
      else if (data?.error) msg = data.error;
      else if (err.message) msg = err.message;
      setHoursError(msg);
    } finally {
      setHoursSaving(false);
    }
  };

  const handleInviteEA = async () => {
    if (!eaName || !eaEmail) {
      setEaError('Name and email are required');
      return;
    }
    setEaInviting(true);
    setEaError(null);
    try {
      // Generate a secure temporary password
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
      let pw = '';
      const arr = new Uint8Array(20);
      crypto.getRandomValues(arr);
      for (let i = 0; i < 20; i++) pw += chars[arr[i] % chars.length];

      await authApi.registerUser({
        email: eaEmail,
        password: pw,
        displayName: eaName,
        tenantId: user?.tenantId || '',
        role: 'ExecutiveAssistant',
      });
      setEaInvited(true);
      setTimeout(() => setActiveStep(4), 500);
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      setEaError(errors ? (Array.isArray(errors) ? errors.join(', ') : String(errors)) : 'Failed to invite EA');
    } finally {
      setEaInviting(false);
    }
  };

  // Format minute of day to HH:MM display
  const formatMinuteOfDay = (m: number) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
  };

  const steps = [
    {
      label: 'Welcome',
      icon: <CelebrationIcon />,
      content: (
        <Box>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Welcome to Cadence, {displayName}!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Let's get you set up in 3 minutes. Cadence will help you govern your time, focus, and strategic execution through structured daily operating rhythms.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
            You can customise your branding (logo, colours, product name) later in Organisation settings.
          </Typography>
          <Button variant="contained" onClick={() => setActiveStep(1)}>
            Let's Get Started
          </Button>
        </Box>
      ),
    },
    {
      label: 'Connect Your Calendar',
      icon: <CalendarIcon />,
      content: (
        <Box>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Connect your work calendar so Cadence can plan your day around your meetings. Private calendar events will be treated as priority meetings.
          </Typography>

          {calendarError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCalendarError(null)}>{calendarError}</Alert>
          )}

          {calendarConnected ? (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
              Calendar connected successfully!
            </Alert>
          ) : (
            <>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<MicrosoftIcon />}
                  onClick={() => handleConnectCalendar('microsoft')}
                  disabled={connecting !== null}
                  fullWidth
                  sx={{
                    bgcolor: '#0078D4',
                    '&:hover': { bgcolor: '#106EBE' },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    justifyContent: 'flex-start',
                    pl: 3,
                  }}
                >
                  {connecting === 'microsoft' ? 'Connecting...' : 'Connect Microsoft 365'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<GoogleIcon />}
                  onClick={() => handleConnectCalendar('google')}
                  disabled={connecting !== null}
                  fullWidth
                  sx={{
                    bgcolor: '#1A1A1A',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': { bgcolor: '#2D2D2D' },
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1.5,
                    justifyContent: 'flex-start',
                    pl: 3,
                  }}
                >
                  {connecting === 'google' ? 'Connecting...' : 'Connect Google Calendar'}
                </Button>
              </Stack>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPersonal}
                    onChange={e => setIsPersonal(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    This is a personal calendar
                  </Typography>
                }
                sx={{ mb: 2 }}
              />

              <Button
                variant="text"
                startIcon={<SkipIcon />}
                onClick={() => { setCalendarSkipped(true); setActiveStep(2); }}
                sx={{ color: 'text.secondary' }}
              >
                Skip -- I'll add an ICS feed later
              </Button>
            </>
          )}
        </Box>
      ),
    },
    {
      label: 'Set Your Working Hours',
      icon: <ScheduleIcon />,
      content: (
        <Box>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            These help Cadence schedule your work in the right time slots and protect your lunch break.
          </Typography>

          {hoursError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHoursError(null)}>{hoursError}</Alert>
          )}
          {hoursSaved && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
              Working hours saved!
            </Alert>
          )}

          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Day Start"
                type="time"
                fullWidth
                value={workDayStart}
                onChange={e => setWorkDayStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Day End"
                type="time"
                fullWidth
                value={workDayEnd}
                onChange={e => setWorkDayEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Lunch Start"
                type="time"
                fullWidth
                value={formatMinuteOfDay(lunchStartMinute)}
                onChange={e => {
                  const [h, m] = e.target.value.split(':').map(Number);
                  if (!isNaN(h) && !isNaN(m)) setLunchStartMinute(h * 60 + m);
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Lunch Duration (min)"
                type="number"
                fullWidth
                value={lunchDurationMinutes}
                onChange={e => setLunchDurationMinutes(parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 120 }}
              />
            </Stack>
          </Stack>

          <Button
            variant="contained"
            onClick={handleSaveHours}
            disabled={hoursSaving || hoursSaved}
            sx={{ mt: 3 }}
          >
            {hoursSaving ? 'Saving...' : hoursSaved ? 'Saved' : 'Save Working Hours'}
          </Button>
        </Box>
      ),
    },
    {
      label: 'Invite Your EA',
      icon: <PersonAddIcon />,
      content: (
        <Box>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Do you have an Executive Assistant? Invite them to help manage your day. They will be able to create work items, manage your plan, and upload images on your behalf.
          </Typography>

          {eaError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEaError(null)}>{eaError}</Alert>
          )}
          {eaInvited && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
              Invitation sent to {eaEmail}! They will receive a welcome email with login instructions.
            </Alert>
          )}

          {!eaInvited && (
            <>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="EA Name"
                  fullWidth
                  value={eaName}
                  onChange={e => setEaName(e.target.value)}
                  placeholder="e.g. Sarah Johnson"
                />
                <TextField
                  label="EA Email"
                  type="email"
                  fullWidth
                  value={eaEmail}
                  onChange={e => setEaEmail(e.target.value)}
                  placeholder="ea@company.com"
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleInviteEA}
                  disabled={eaInviting}
                >
                  {eaInviting ? 'Inviting...' : 'Invite EA'}
                </Button>
                <Button
                  variant="text"
                  startIcon={<SkipIcon />}
                  onClick={() => { setEaSkipped(true); setActiveStep(4); }}
                  sx={{ color: 'text.secondary' }}
                >
                  Skip -- I'll do this later
                </Button>
              </Stack>
            </>
          )}
        </Box>
      ),
    },
    {
      label: "You're Ready!",
      icon: <CheckIcon />,
      content: (
        <Box>
          <Typography variant="h5" sx={{ mb: 1 }}>
            Your Cadence is set up.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Click "Go to Dashboard" to generate your first daily plan. Cadence will sync your calendar, apply your preferences, and build a structured schedule for your day.
          </Typography>

          {/* Completion checklist */}
          <Card sx={{ mb: 3, bgcolor: 'rgba(255,255,255,0.03)' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Setup Checklist</Typography>
              <Stack spacing={1}>
                <ChecklistItem
                  completed={calendarConnected}
                  skipped={calendarSkipped}
                  label="Calendar connected"
                />
                <ChecklistItem
                  completed={hoursSaved}
                  label="Working hours set"
                />
                <ChecklistItem
                  completed={eaInvited}
                  skipped={eaSkipped}
                  label="EA invited"
                />
              </Stack>
            </CardContent>
          </Card>

          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/')}
          >
            Go to Dashboard
          </Button>
        </Box>
      ),
    },
  ];

  const totalSteps = steps.length;
  const progressPct = Math.round((activeStep / (totalSteps - 1)) * 100);

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>Getting Started</Typography>
          <Typography color="text.secondary">
            Set up your Cadence in a few quick steps
          </Typography>
        </Box>
        <Chip
          label={`Step ${activeStep + 1} of ${totalSteps}`}
          variant="outlined"
          sx={{ fontWeight: 600, mt: 0.5 }}
        />
      </Box>

      {/* Progress bar */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.06)' }}>
          <Box sx={{ width: `${progressPct}%`, bgcolor: 'primary.main', borderRadius: 3, transition: 'width 0.4s ease' }} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
          {progressPct}% complete
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => (
          <Step key={step.label} completed={index < activeStep}>
            <StepLabel
              optional={index < activeStep ? (
                <Typography variant="caption" color="success.main">Complete</Typography>
              ) : undefined}
              sx={{ cursor: index < activeStep ? 'pointer' : 'default' }}
              onClick={() => { if (index < activeStep) setActiveStep(index); }}
            >
              <Typography variant="subtitle1" fontWeight={activeStep === index ? 600 : 400} color={activeStep === index ? 'text.primary' : 'text.secondary'}>
                {step.label}
              </Typography>
            </StepLabel>
            <StepContent>
              <Box sx={{ py: 1 }}>
                {step.content}
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      {/* Skip all button */}
      {activeStep < totalSteps - 1 && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={() => navigate('/')}
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            I'll set up later -- skip to dashboard
          </Button>
        </Box>
      )}
    </Box>
  );
}

function ChecklistItem({ completed, skipped, label }: { completed: boolean; skipped?: boolean; label: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {completed ? (
        <CheckIcon sx={{ fontSize: 20, color: 'success.main' }} />
      ) : skipped ? (
        <SkipIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
      ) : (
        <Box sx={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid', borderColor: 'text.secondary' }} />
      )}
      <Typography
        variant="body2"
        color={completed ? 'success.main' : skipped ? 'text.secondary' : 'text.primary'}
        sx={{ textDecoration: skipped ? 'line-through' : 'none' }}
      >
        {label}{skipped ? ' (skipped)' : ''}
      </Typography>
    </Box>
  );
}
